import { FIXED_LIMITS } from '@lagerfeuer/shared';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { checkImage, IMAGE_ACCEPT, useObjectUrl } from '../../components/imageFiles';
import { Modal } from '../../components/Modal';
import { errorMessage } from '../../lib/api';
import { serversApi } from '../../lib/endpoints';
import { paths } from '../../lib/routes';
import { useAppStore } from '../../store/app';
import { overlays } from '../../store/overlays';
import { displayName } from '../../store/selectors';
import { toast } from '../../store/ui';
import { addJoinedServer } from './inviteCode';
import './servers.css';

/** Neuen Server anlegen (Name, optional Icon und Beschreibung) und direkt hineinwechseln. */
export function CreateServerDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const me = useAppStore((s) => s.me);
  const maxIcon = useAppStore((s) => s.limits.maxAvatarBytes);
  const [name, setName] = useState(() => (me ? `${displayName(me)}s Server` : ''));
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const preview = useObjectUrl(icon);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    setError(null);
    try {
      let full = await serversApi.create(description.trim() ? { name: n, description: description.trim() } : { name: n });
      if (icon) {
        try {
          const updated = await serversApi.setIcon(full.id, icon);
          full = { ...full, ...updated };
        } catch (err) {
          toast.error('Icon konnte nicht hochgeladen werden', errorMessage(err));
        }
      }
      addJoinedServer(full);
      onClose();
      void navigate(full.systemChannelId ? paths.channel(full.id, full.systemChannelId) : paths.server(full.id));
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Server erstellen"
      onClose={onClose}
      testId="create-server-dialog"
      footer={
        <>
          <button type="button" className="btn btn-link" onClick={() => overlays.open({ type: 'joinServer' })} data-testid="create-server-to-join">
            Stattdessen beitreten
          </button>
          <span className="spacer" />
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" form="create-server-form" className="btn btn-primary" disabled={busy || !name.trim()} data-testid="create-server-submit">
            {busy ? 'Wird erstellt …' : 'Erstellen'}
          </button>
        </>
      }
    >
      <form id="create-server-form" className="stack" onSubmit={submit} noValidate>
        <p className="muted" style={{ margin: 0 }}>
          Dein Server ist dein Lagerfeuer – lade Freunde ein und gib ihm einen Namen und ein Icon. Du kannst beides später ändern.
        </p>
        <div className="field">
          <label htmlFor="create-server-name">Servername</label>
          <input
            id="create-server-name"
            className="input"
            value={name}
            maxLength={FIXED_LIMITS.serverNameMax}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
            data-testid="create-server-name"
          />
        </div>
        <div className="create-server-icon">
          <label className="icon-picker" data-testid="create-server-icon">
            {preview ? <img src={preview} alt="Icon-Vorschau" /> : <span className="icon-picker-empty">Icon</span>}
            <input
              type="file"
              accept={IMAGE_ACCEPT}
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (!f) return;
                const err = checkImage(f, maxIcon);
                setError(err);
                if (!err) setIcon(f);
              }}
              data-testid="create-server-icon-input"
            />
          </label>
          {icon && (
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setIcon(null)} data-testid="create-server-icon-remove">
              Icon entfernen
            </button>
          )}
        </div>
        <div className="field">
          <label htmlFor="create-server-desc">Beschreibung (optional)</label>
          <textarea id="create-server-desc" className="textarea" rows={2} maxLength={1000} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="create-server-description" />
        </div>
        {error && (
          <p className="error-text" role="alert" data-testid="create-server-error">
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
}
